<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-newspaper" class="q-mb-sm" /> {{ $t(title_news) }}
		</div>

		<q-tabs v-model="tab_content">
			<q-tab slot="title" name="news" :label="$t('news')" />
			<q-tab
				slot="title"
				v-if="useCommentsNews"
				name="comments"
				:label="$t('comments')"
				v-show="showTab"
			/>

			<q-tab-pane name="news">
				<div class="row gutter-sm q-mb-lg">
					<div class="col-xs-12 col-sm-12">
						<q-field :count="256">
							<q-input
								v-model="title"
								ref="title"
								:float-label="$t('title')"
								:error="$v.title.$error"
								@input="$v.title.$touch"
							/>
						</q-field>
					</div>
					<div class="col-xs-12 col-sm-12">
						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-6">
								<p class="caption">{{ $t('image') }}</p>
								<uploader
									content_type="images"
									@fileUploaded="imageUploaded"
									:aspect_ratio="52 / 29"
									:aspect_ratio_string="'52/29'"
									:multiple="false"
									:cropper="true"
								/>

								<p v-if="image_url" class="q-mt-md file-url">
									<q-icon name="fal fa-check" color="positive" />
									<a :href="image_url" target="_blank">
										{{ $truncateLink(image_url) }}</a
									>
								</p>
							</div>
							<div class="col-xs-12 col-sm-6">
								<p class="caption">
									{{ $t('alt_image') }}
									<q-icon
										name="fal fa-question-circle"
										size="1.5rem"
										color="grey"
										class="q-ml-sm q-pb-sm"
									>
										<q-tooltip
											class="custom-tooltip"
											anchor="top left"
											self="bottom right"
										>
											{{ $t('alt_image_information_helper') }}
										</q-tooltip>
									</q-icon>
								</p>
								<div class="flex justify-between">
									<template v-for="lang in languages" class="row flex">
										<q-field
											:count="170"
											:key="lang.id"
											v-if="lang.code === 'pt-br'"
										>
											<q-input
												v-model="language_br"
												:float-label="$t(`${lang.name}`)"
												ref="language_br"
												:error="$v.language_br.$error"
												@input="$v.language_br.$touch"
											/>
										</q-field>
										<q-field
											:count="170"
											:key="lang.id"
											v-if="lang.code === 'en-us'"
										>
											<q-input
												v-model="language_us"
												:float-label="$t(`${lang.name}`)"
												ref="language_us"
												:error="$v.language_us.$error"
												@input="$v.language_us.$touch"
											/>
										</q-field>
										<q-field
											:count="170"
											:key="lang.id"
											v-if="lang.code === 'es-es'"
										>
											<q-input
												v-model="language_es"
												:float-label="$t(`${lang.name}`)"
												ref="language_es"
												:error="$v.language_es.$error"
												@input="$v.language_es.$touch"
											/>
										</q-field>
										<q-field
											:count="170"
											:key="lang.id"
											v-if="lang.code === 'fr-fr'"
										>
											<q-input
												v-model="language_fr"
												:float-label="$t(`${lang.name}`)"
												ref="language_fr"
												:error="$v.language_fr.$error"
												@input="$v.language_fr.$touch"
											/>
										</q-field>
									</template>
								</div>
							</div>
						</div>
					</div>
					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('content') }}</p>
						<q-editor
							v-model="content"
							ref="content"
							:toolbar="[
								['bold', 'italic', 'strike', 'underline'],
								['left', 'center', 'right', 'justify'],
								['link', 'upload'],
								[
									{
										label: null,
										icon: 'fas fa-font',
										fixedLabel: true,
										fixedIcon: true,
										list: 'no-icons',
										options: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code'],
									},
									{
										label: null,
										icon: 'fas fa-text-height',
										fixedLabel: true,
										fixedIcon: true,
										list: 'no-icons',
										options: [
											'size-1',
											'size-2',
											'size-3',
											'size-4',
											'size-5',
											'size-6',
											'size-7',
										],
									},
									'removeFormat',
								],
								['quote', 'unordered', 'ordered'],
								['custom_btn'],
							]"
							:definitions="{
								bold: { label: null, icon: 'fas fa-bold' },
								italic: { label: null, icon: 'fas fa-italic' },
								strike: { label: null, icon: 'fas fa-strikethrough' },
								underline: { label: null, icon: 'fas fa-underline' },
								left: { label: null, icon: 'fas fa-align-left' },
								center: { label: null, icon: 'fas fa-align-center' },
								right: { label: null, icon: 'fas fa-align-right' },
								justify: { label: null, icon: 'fas fa-align-justify' },
								link: { label: null, icon: 'fas fa-link' },
								upload: {
									tip: null,
									icon: 'fas fa-file-image',
									handler: insertImg,
								},
								removeFormat: { label: null, icon: 'fas fa-eraser' },
								quote: { label: null, icon: 'fas fa-quote-right' },
								unordered: { label: null, icon: 'fas fa-list-ul' },
								ordered: { label: null, icon: 'fas fa-list-ol' },
							}"
						>
							<q-btn
								slot="custom_btn"
								dense
								flat
								color="black"
								icon="fal fa-code"
								label="HTML"
								@click="show_html = true"
							/>
						</q-editor>
					</div>

					<div class="col-12">
						<q-select
							v-model="timezone"
							ref="timezone"
							:options="timezones"
							:float-label="$t('timezone')"
							filter
							:error="$v.timezone.$error"
							@input="$v.timezone.$touch"
						/>
					</div>

					<div class="col-xs-12 col-sm-6">
						<q-datetime
							v-model="publish_date"
							ref="publish_date"
							type="datetime"
							:float-label="$t('publish_date')"
							format="DD/MM/YYYY HH:mm"
						/>
					</div>

					<div class="col-xs-12 col-sm-6">
						<q-datetime
							v-model="unpublish_date"
							ref="unpublish_date"
							type="datetime"
							:float-label="$t('unpublish_date')"
							format="DD/MM/YYYY HH:mm"
						/>
					</div>

					<div class="col-xs-12 col-sm-12">
						<q-select
							v-model="tag"
							:options="optionTags"
							:float-label="$t('tags')"
							multiple
							chips
							filter
							autofocus-filter
						/>
					</div>
				</div>

				<q-tabs v-model="tab" color="secondary" class="shadow-1">
					<q-tab
						slot="title"
						name="permissions"
						icon="fal fa-clipboard-list-check"
						:label="$t('permissions')"
					/>

					<q-tab-pane name="permissions" class="q-px-lg q-pb-xl">
						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<p class="caption">{{ $t('areas') }}</p>
								<q-checkbox
									class="q-mb-sm"
									v-model="all_areas"
									:val="true"
									:label="$t('select_all_areas')"
									@input="checkAllAreas()"
								/>
								<select-multiple
									:filters="area"
									:options="areas"
									@on_value="area = $event"
									:error="$v.area.$error"
									:input="$v.area.$touch"
								/>
							</div>

							<div class="col-xs-12 col-sm-12">
								<p class="caption">{{ $t('profiles') }}</p>

								<div class="row gutter-sm">
									<div class="col-xs-12 col-sm-12">
										<q-checkbox
											v-model="all_profiles"
											:val="true"
											:label="$t('all')"
											@input="checkAll()"
										/>
									</div>

									<div
										class="col-xs-12 col-sm-4"
										v-for="(item, index) in profiles"
										:key="index"
										:class="getErrorClass($v.profile.$error)"
									>
										<q-checkbox
											keep-color
											v-model="profile"
											ref="profile"
											:val="item.value"
											:label="item.label"
											@input="$v.profile.$touch"
											:color="getErrorColor($v.profile.$error)"
										/>
									</div>

									<div class="col-xs-12 col-sm-12">
										<q-select
											v-model="user_group"
											ref="user_group"
											:options="user_groups"
											:float-label="$t('groups')"
											:error="$v.user_group.$error"
											@input="$v.user_group.$touch"
											multiple
											chips
										/>
									</div>
								</div>
							</div>
						</div>
					</q-tab-pane>
				</q-tabs>

				<div class="row gutter-sm q-my-sm" v-if="id_news != ''">
					<div class="col-xs-12 col-sm-11">
						<q-input v-model="url_news" :float-label="$t('link')" :disable="true" />
					</div>

					<div class="col-xs-12 col-sm-1 q-mt-lg">
						<q-btn
							dense
							color="primary"
							icon="fal fa-copy"
							class="full-width"
							@click="copyLink()"
						>
							<q-tooltip>
								{{ $t('copy_link') }}
							</q-tooltip>
						</q-btn>
					</div>
				</div>

				<div class="row gutter-sm q-my-sm">
					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('status') }}</p>
						<q-btn-toggle
							v-model="status"
							:toggle-color="getColorStatus"
							:options="statuses"
						/>
					</div>
				</div>

				<div class="row gutter-sm justify-end q-mt-lg">
					<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
						<q-btn
							color="tertiary"
							class="full-width"
							:label="$t('back')"
							icon="far fa-chevron-left"
							@click="$router.push({ name: 'admin_news' })"
						/>
					</div>

					<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
						<q-btn
							color="secondary"
							class="full-width"
							:label="$t('save')"
							icon="far fa-save"
							@click="saveData()"
						/>
					</div>
				</div>
			</q-tab-pane>

			<q-tab-pane name="comments">
				<comments origin="news" />
			</q-tab-pane>
		</q-tabs>

		<q-modal
			v-model="show_html"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_html = false" icon="fas fa-times" />
					<q-toolbar-title>
						HTML
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row">
						<div class="col-12">
							<q-input
								type="textarea"
								rows="10"
								float-label="HTML"
								v-model="content"
							/>
						</div>
					</div>

					<div class="row gutter-sm justify-end q-mt-lg">
						<div class="col-xs-12 col-sm-3">
							<q-btn
								color="tertiary"
								class="full-width"
								:label="$t('close')"
								icon="far fa-ban"
								@click="show_html = false"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, requiredIf, minLength, maxLength } from 'vuelidate/lib/validators'
import { date } from 'quasar'
import Uploader from 'components/Uploader'
import Comments from 'components/admin/Comments'
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import { BlobServiceClient } from '@azure/storage-blob'
import SelectMultiple from 'components/utils/SelectMultiple'
export default {
	i18nOptions: { namespaces: ['custom', 'common', 'news', 'admin'] },

	components: {
		uploader: Uploader,
		comments: Comments,
		'select-multiple': SelectMultiple,
	},

	data() {
		return {
			language_br: '',
			language_us: '',
			language_es: '',
			language_fr: '',
			id_news: '',
			image_url_complete: '',
			image_url: '',
			title: '',
			content: '',
			publish_date: '',
			unpublish_date: '',
			timezone: '',
			status: 1,
			statuses: [
				{ label: this.$t('published'), value: 1 },
				{ label: this.$t('unpublished'), value: 2 },
			],
			area: [],
			profile: [],
			tag: [],
			user_group: [],
			tab: 'permissions',
			tab_content: 'news',
			all_profiles: false,
			show_html: false,
			all_areas: false,

			// Q-editor Image to be uploaded
			upload_url: process.env.API_URL + '/files/policy/',
			urls: [],
			cancel_token: '',
			source: '',
			crop_modal: false,
			original_image: '',
			fileUrl: '',
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			areas: 'registration_lists/country_areas',
			user_groups: 'registration_lists/group_users',
			profiles: 'registration_lists/profiles',
			tags: 'registration_lists/tags',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			timezones: 'registration_lists/timezones',
			languages: 'params/languages',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		optionTags() {
			if (this.tags.length > 0) {
				return this.tags.map((t) => {
					let areas = t.areas.map((a) => a.name)
					return {
						value: t.value,
						label: t.label,
						sublabel: areas.join(', '),
					}
				})
			} else return []
		},

		getColorStatus() {
			if (this.status == 1) return 'positive'
			else return 'negative'
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},

		title_news() {
			if (this.id_user_client_selected) {
				let obj = this.clients.find((obj) => {
					return obj.id === this.id_user_client_selected
				})
				if (obj) {
					return obj.title_news == '' ? 'news' : obj.title_news
				}
			}
		},

		showTab() {
			if (this.id_news != 0) return true
			else return false
		},

		useCommentsNews() {
			let obj = this.clients.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (obj) return obj.use_comments_news
			else return true
		},

		url_news: {
			get() {
				let url = process.env.SITE_URL ? process.env.SITE_URL : document.location.origin
				return `${url}/news/${this.id_news}`
			},
			set() {
				''
			},
		},
	},

	methods: {
		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				let data = {
					title: this.title,
					image_url: this.image_url,
					content: this.$clearEmoticons(this.content),
					status: this.status,
					news_image_alt_texts: {
						'pt-br': this.language_br ? this.language_br : '',
						'en-us': this.language_us ? this.language_us : '',
						'es-es': this.language_es ? this.language_es : '',
						'fr-fr': this.language_fr ? this.language_fr : '',
					},
				}

				this.timezone ? (data.timezone = this.timezone) : (data.timezone = null)

				this.publish_date
					? (data.publish_date = moment(
							date.formatDate(this.publish_date, 'YYYY-MM-DDTHH:mm')
					  )
							.tz(this.timezone ? this.timezone : 'Etc/GMT', true)
							.format('YYYY-MM-DDTHH:mm:ssZ'))
					: (data.publish_date = null)

				this.unpublish_date
					? (data.unpublish_date = moment(
							date.formatDate(this.unpublish_date, 'YYYY-MM-DDTHH:mm')
					  )
							.tz(this.timezone ? this.timezone : 'Etc/GMT', true)
							.format('YYYY-MM-DDTHH:mm:ssZ'))
					: (data.unpublish_date = null)

				if (this.tag.length) data.tags = this.tag
				if (this.area.length > 0) data.areas = this.area
				if (this.profile.length > 0) data.profiles = this.profile
				if (this.user_group.length > 0) data.user_groups = this.user_group

				if (this.id_news != 0) {
					this.$axios
						.put(`/admin_news/news/${this.id_news}/`, data, this.getHeader)
						.then((response) => {
							this.$q.loading.hide()

							this.$q.notify({
								message: this.$t('news_edit_success'),
								type: 'positive',
								icon: 'fal fa-save',
							})

							this.$router.push({ name: 'admin_news' })
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				} else {
					this.$axios
						.post(`/admin_news/news/`, data, this.getHeader)
						.then((response) => {
							this.$q.loading.hide()

							this.$q.notify({
								message: this.$t('news_add_success'),
								type: 'positive',
								icon: 'fal fa-save',
							})

							this.$router.push({ name: 'admin_news' })
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				}
			} else {
				this.$formInputErrorFocus(this.$v, this.$refs)
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fal fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		imageUploaded(url) {
			this.image_url = url
			this.image_url_complete = url
		},

		fetchData() {
			this.$q.loading.show()
			this.$axios
				.get(`/admin_news/news/${this.id_news}/`, this.getHeader)
				.then((response) => {
					this.title = response.data.title
					this.content = response.data.content ? response.data.content : ''

					if (response.data.publish_date) this.publish_date = response.data.publish_date
					if (response.data.unpublish_date)
						this.unpublish_date = response.data.unpublish_date
					if (response.data.timezone) this.timezone = response.data.timezone

					this.status = response.data.status
					this.tag = response.data.tags
					this.profile = response.data.profiles
					this.area = response.data.areas
					this.user_group = response.data.user_groups
					this.image_url = response.data.image_url

					const altTextMap = {}
					response.data.news_image_alt_texts.forEach((altTextObj) => {
						altTextMap[altTextObj.language] = altTextObj.alt_text
					})
					this.language_br = altTextMap['pt-br'] || ''
					this.language_us = altTextMap['en-us'] || ''
					this.language_es = altTextMap['es-es'] || ''
					this.language_fr = altTextMap['fr-fr'] || ''

					this.$q.loading.hide()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		insertImg() {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = '.png, .jpg, .gif'
			let file

			input.onchange = (_) => {
				const files = Array.from(input.files)
				file = files[0]

				this.uploadFile(file)
			}
			input.click()
		},

		uploadFile(file) {
			let date = moment()

			let fd = new FormData()
			fd.append('filename', file.name)
			fd.append(
				'file_directory',
				`media/html_images/news/${date.year()}/${date.month() + 1}/`
			)

			this.$axios
				.post('/api/files/policy/', fd, this.getHeader, { cancelToken: this.source.token })
				.then((response) => {
					return new Promise((resolve) => {
						this.$q.loading.show()

						if (response.data.origin === 'azure') {
							const blobServiceClient = new BlobServiceClient(
								`${response.data.base_url}?${response.data.sas_token}`
							)

							const main = async () => {
								const container_client = blobServiceClient.getContainerClient('')

								const block_blob_client = container_client.getBlockBlobClient(
									response.data.filename
								)
								const upload_blob_response = await block_blob_client.uploadBrowserData(
									file
								)

								let fd_complete = new FormData()
								fd_complete.append('uploaded', true)
								fd_complete.append('fileSize', file.size)
								fd_complete.append('file', response.data.file_id)

								if (this.custom_directory) {
									fd_complete.append('not_signed', true)
									fd_complete.append('bucket_name', 'static')
								}

								this.$axios
									.post('/api/files/complete/', fd_complete, this.getHeader)
									.then((response) => {
										this.fileUrl = response.data.fileUrl
										document.execCommand(
											'insertHTML',
											true,
											'<div><img class="uploadBase" src="' +
												this.fileUrl +
												'" style="max-width: 100%" /></div>'
										)
									})
									.finally(() => {
										resolve()
										this.$q.loading.hide()
									})
							}

							main()
						} else {
							let content_type =
								file.type != '' ? file.type : 'application/octet-stream'
							let policy = response.data
							let file_name = policy.filename
							let key_path = policy.file_bucket_path

							let fd_s3 = new FormData()
							fd_s3.append('key', key_path + file_name)
							fd_s3.append('acl', 'private')
							fd_s3.append('Content-Type', content_type)
							fd_s3.append('AWSAccessKeyId', policy.key)
							fd_s3.append('Policy', policy.policy)
							fd_s3.append('filename', file_name)
							fd_s3.append('Signature', policy.signature)
							fd_s3.append('file', file)

							this.cancel_token = this.$axios.CancelToken
							this.source = this.cancel_token.source()

							this.$axios
								.post(process.env.BUCKET_URL, fd_s3, {
									// onUploadProgress: function( event ) {
									//     updateProgress(event.loaded / event.total)
									// },
									// cancelToken: this.source.token
								})
								.then((response) => {
									let fd_complete = new FormData()
									fd_complete.append('uploaded', true)
									fd_complete.append('fileSize', file.size)
									fd_complete.append('file', policy.file_id)

									this.$axios
										.post('/api/files/complete/', fd_complete, this.getHeader)
										.then((response) => {
											let data = {
												file_url: response.data.fileUrlSigned,
											}

											this.$axios
												.post('/utils/presigned_url/', data, this.getHeader)
												.then((response) => {
													console.log(response.data.file_url)
													document.execCommand(
														'insertHTML',
														true,
														`<div><img class="uploadBase" src="${
															response.data.file_url
														}" style="max-width: 100%" /></div>`
													)
												})
												.catch((e) => {
													console.log(e)
												})
												.finally(() => {
													this.$q.loading.hide()
												})
										})
										.finally(() => {
											this.$q.loading.hide()
										})
								})
								.finally(() => {
									resolve()
								})
						}
					})
				})
				.catch((e) => {
					console.log(e)
					this.$q.loading.hide()
				})
		},

		checkAll() {
			if (this.all_profiles) {
				this.profiles.forEach((element) => {
					this.profile.push(element.value)
				})
			} else {
				this.profile = []
			}
		},

		checkAllAreas() {
			if (this.all_areas) {
				this.area = this.areas.map((e) => e.value)
			} else {
				this.area = []
			}
		},

		getErrorColor(state) {
			if (state) return 'negative'
			else return ''
		},

		getErrorClass(state) {
			if (state) return 'checkbox-error'
			else return ''
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		copyLink() {
			this.$copyText(this.url_news).then(
				(e) => {
					this.$q.notify({
						message: this.$t('link_copied_to_clipboard'),
						type: 'positive',
						icon: 'fal fa-copy',
					})
				},
				(e) => {
					console.log(e)
				}
			)
		},

		fetchTimezones() {
			let values = []
			moment.tz
				.names()
				.reduce((memo, tz) => {
					memo.push({
						name: tz,
						offset: moment.tz(tz).utcOffset(),
					})
					return memo
				}, [])
				.sort((a, b) => {
					return a.offset - b.offset
				})
				.reduce((memo, tz) => {
					const timezone = tz.offset ? moment.tz(tz.name).format('Z') : ''
					values.push({ value: tz.name, label: `(GMT${timezone}) ${tz.name}`, ...tz })
				}, '')
			this.$store.dispatch('registration_lists/SET_TIMEZONES', [values])
		},
	},

	created() {
		this.fetchTimezones()
		this.id_news = this.$route.params.id
		if (this.id_news != 0) this.fetchData()
	},

	watch: {
		$route: function(refreshPage) {
			this.id_news = this.$route.params.id
			if (this.id_news != 0) this.fetchData()
		},
	},

	validations() {
		return {
			title: { required, maxLength: maxLength(256) },
			language_br: { maxLength: maxLength(256) },
			language_us: { maxLength: maxLength(256) },
			language_es: { maxLength: maxLength(256) },
			language_fr: { maxLength: maxLength(256) },
			content: { required, minLength: minLength(1) },
			area: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.area.length == 0) {
						if (
							this.profile.length == 0 &&
							this.area.length == 0 &&
							this.user_group.length == 0
						)
							return true
						else return false
					} else {
						return true
					}
				}),
			},

			timezone: {
				requiredIf: requiredIf(() => {
					if (this.publish_date || this.unpublish_date) return true
					else return false
				}),
			},

			profile: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.area.length == 0) {
						if (
							this.profile.length == 0 &&
							this.area.length == 0 &&
							this.user_group.length == 0
						)
							return true
						else return false
					} else {
						return true
					}
				}),
			},
			user_group: {
				requiredIf: requiredIf(() => {
					if (
						this.profile.length == 0 &&
						this.area.length == 0 &&
						this.user_group.length == 0
					) {
						return true
					} else {
						return false
					}
				}),
			},
		}
	},
}
</script>

<style></style>
